import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const roles = [
  { label: 'Full-Stack Engineer',   color: '#00ffff' },
  { label: 'Network Architect',     color: '#ff0033' },
  { label: 'Systems Builder',       color: '#b388ff' },
  { label: 'Security Specialist',   color: '#69f0ae' },
  { label: 'Problem Solver',        color: '#ffd740' },
];

const skills = [
  { tag: 'React',        color: '#61dafb' },
  { tag: 'Java',         color: '#ff6b35' },
  { tag: 'Next.js',      color: '#ffffff' },
  { tag: 'PostgreSQL',   color: '#336791' },
  { tag: 'Python',       color: '#ffd343' },
  { tag: 'Docker',       color: '#2496ed' },
  { tag: 'Kubernetes',   color: '#326ce5' },
  { tag: 'FastAPI',      color: '#009688' },
  { tag: 'TypeScript',   color: '#3178c6' },
  { tag: 'Tailwind',     color: '#38bdf8' },
  { tag: 'MongoDB',      color: '#47a248' },
  { tag: 'Terraform',    color: '#7b42bc' },
];

const TypeWriter = ({ texts, speed = 60, pause = 1800 }) => {
  const [display, setDisplay] = useState('');
  const [idx, setIdx]         = useState(0);
  const [charIdx, setCharIdx] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const color = texts[idx]?.color || '#00ffff';

  useEffect(() => {
    const current = texts[idx]?.label || '';
    let timer;
    if (!deleting && charIdx < current.length) {
      timer = setTimeout(() => {
        setDisplay(current.slice(0, charIdx + 1));
        setCharIdx(c => c + 1);
      }, speed);
    } else if (!deleting && charIdx === current.length) {
      timer = setTimeout(() => setDeleting(true), pause);
    } else if (deleting && charIdx > 0) {
      timer = setTimeout(() => {
        setDisplay(current.slice(0, charIdx - 1));
        setCharIdx(c => c - 1);
      }, speed / 2);
    } else if (deleting && charIdx === 0) {
      setDeleting(false);
      setIdx(i => (i + 1) % texts.length);
    }
    return () => clearTimeout(timer);
  }, [charIdx, deleting, idx, texts, speed, pause]);

  return (
    <Box component="span" sx={{ color, transition: 'color 0.3s' }}>
      {display}
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          width: '2px',
          height: '1.1em',
          background: color,
          ml: '2px',
          verticalAlign: 'middle',
          animation: 'blink 1s step-end infinite',
          '@keyframes blink': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0 },
          },
        }}
      />
    </Box>
  );
};

const SkillPill = ({ tag, color }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <Box
      component="span"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '5px',
        fontSize: '11px',
        fontFamily: 'JetBrains Mono, monospace',
        px: '10px',
        py: '4px',
        borderRadius: '100px',
        border: `1px solid ${hovered ? color : 'rgba(255,255,255,0.12)'}`,
        color: hovered ? color : 'rgba(255,255,255,0.55)',
        background: hovered ? `${color}14` : 'rgba(255,255,255,0.04)',
        cursor: 'default',
        transition: 'all 0.2s ease',
        userSelect: 'none',
      }}
    >
      <Box
        sx={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: color,
          opacity: hovered ? 1 : 0.4,
          transition: 'opacity 0.2s',
        }}
      />
      {tag}
    </Box>
  );
};

const StatCard = ({ value, label, accent }) => (
  <Box
    sx={{
      flex: 1,
      minWidth: 0,
      p: '12px 14px',
      borderRadius: '12px',
      border: `1px solid rgba(255,255,255,0.08)`,
      background: 'rgba(255,255,255,0.03)',
      textAlign: 'center',
      transition: 'border-color 0.2s',
      '&:hover': { borderColor: accent },
    }}
  >
    <Typography
      sx={{
        fontFamily: 'JetBrains Mono, monospace',
        fontSize: { xs: '20px', md: '26px' },
        fontWeight: 700,
        color: accent,
        lineHeight: 1,
        mb: '4px',
      }}
    >
      {value}
    </Typography>
    <Typography
      sx={{
        fontSize: '10px',
        fontFamily: 'Inter, sans-serif',
        color: 'rgba(255,255,255,0.4)',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
      }}
    >
      {label}
    </Typography>
  </Box>
);

const TechIntro = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      sx={{
        display: { xs: 'flex', md: 'flex', lg: 'none' },
        flexDirection: 'column',
        px: 2,
        gap: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle grid bg accent */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(0,255,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,255,0.03) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Terminal prompt line */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          mb: '10px',
        }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#ff0033',
            boxShadow: '0 0 8px #ff003388',
          }}
        />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#ffd740' }} />
        <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: '#69f0ae' }} />
        <Typography
          sx={{
            ml: 1,
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '10px',
            color: 'rgba(255,255,255,0.25)',
          }}
        >
          ~/portfolio — zsh
        </Typography>
      </Box>

      {/* Main headline */}
      <Box sx={{ position: 'relative', zIndex: 1, mb: '6px' }}>
        <Typography
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: { xs: '11px', md: '12px' },
            color: '#00ffff',
            mb: '4px',
            letterSpacing: '0.1em',
          }}
        >
          $ whoami
        </Typography>
        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '26px', md: '32px' },
            fontWeight: 800,
            color: '#e6edf3',
            lineHeight: 1.15,
            fontFamily: 'JetBrains Mono, monospace',
            mb: '2px',
          }}
        >
          Your Tech.{' '}
          <Box
            component="span"
            sx={{
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                left: 0,
                bottom: '-2px',
                width: '100%',
                height: '2px',
                background: 'linear-gradient(90deg, #ff0033, #00ffff)',
                borderRadius: '2px',
              },
            }}
          >
            Solutions
          </Box>{' '}
          Partner.
        </Typography>
      </Box>

      {/* Typewriter role */}
      <Box sx={{ position: 'relative', zIndex: 1, mb: '16px', minHeight: '28px' }}>
        <Typography
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: { xs: '14px', md: '16px' },
            color: 'rgba(255,255,255,0.5)',
          }}
        >
          <Box component="span" sx={{ color: 'rgba(255,255,255,0.25)' }}>
            {'>'}{' '}
          </Box>
          <TypeWriter texts={roles} />
        </Typography>
      </Box>

      {/* One-liner */}
      <Typography
        sx={{
          position: 'relative',
          zIndex: 1,
          fontFamily: 'Inter, sans-serif',
          fontSize: { xs: '13px', md: '14px' },
          color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.7,
          mb: '20px',
          borderLeft: '2px solid rgba(0,255,255,0.35)',
          pl: '12px',
        }}
      >
        Building enterprise systems, billing platforms, and cloud infrastructure — turning complex
        problems into clean, scalable software.
      </Typography>

      {/* Stats row */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          gap: '8px',
          mb: '20px',
        }}
      >
        <StatCard value="6+" label="Projects Shipped" accent="#00ffff" />
        <StatCard value="3+" label="Years Building" accent="#ff0033" />
        <StatCard value="20+" label="Admin Users" accent="#b388ff" />
      </Box>

      {/* Skills pills */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          mb: '20px',
        }}
      >
        {skills.map((s) => (
          <SkillPill key={s.tag} tag={s.tag} color={s.color} />
        ))}
      </Box>

      {/* CTA buttons */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <Box
          component="a"
          href="#projects"
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '12px',
            px: '16px',
            py: '9px',
            borderRadius: '10px',
            background: 'rgba(0,255,255,0.1)',
            border: '1px solid rgba(0,255,255,0.4)',
            color: '#00ffff',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background 0.18s',
            '&:hover': { background: 'rgba(0,255,255,0.18)' },
          }}
        >
          ls ./projects ↓
        </Box>
        <Box
          component="a"
          href="#contact"
          sx={{
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: '12px',
            px: '16px',
            py: '9px',
            borderRadius: '10px',
            background: 'rgba(255,0,51,0.08)',
            border: '1px solid rgba(255,0,51,0.35)',
            color: '#ff0033',
            textDecoration: 'none',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            transition: 'background 0.18s',
            '&:hover': { background: 'rgba(255,0,51,0.16)' },
          }}
        >
          ./hire-me →
        </Box>
      </Box>
    </Box>
  );
};

export default TechIntro;